import React, { useEffect, useRef, useState } from 'react';
import bgPattern from "../assets/Onboarding-Form.jpg";
import { useNavigate } from 'react-router-dom';

type StorefrontFormData = {
  firstName: string;
  lastName: string;
  email: string;
  brandName: string;
  website: string;
  productCountAndCategories: string; // Drive link if provided
  productCountAndCategoriesFileName: string; // Name of uploaded file
  productCountAndCategoriesFileUrl: string; // Object URL for download
  productAsinsOrLinks: string; // Drive link if provided
  productAsinsOrLinksFileName: string; // Name of uploaded file
  productAsinsOrLinksFileUrl: string; // Object URL for download
  bestSellersOrHeroes: string;
  hasBrandMentions: boolean;
  keyFeaturesBenefits: string;
  targetAudience: string;
  competitor1: string;
  competitor2: string;
  competitor3: string;
  closestCompetitor: string;
  competitorLikeDislike: string;
  brandComparison: string;
  brandTonality: string;
  inspirationsReferences: string;
  desiredDesignStyle: string;
  favBrand1Link: string;
  favBrand1Describe: string;
  favBrand2Link: string;
  favBrand2Describe: string;
  favBrand3Link: string;
  favBrand3Describe: string;
};

const StorefrontFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<StorefrontFormData>({
    firstName: '',
    lastName: '',
    email: '',
    brandName: '',
    website: '',
    productCountAndCategories: '',
    productCountAndCategoriesFileName: '',
    productCountAndCategoriesFileUrl: '',
    productAsinsOrLinks: '',
    productAsinsOrLinksFileName: '',
    productAsinsOrLinksFileUrl: '',
    bestSellersOrHeroes: '',
    hasBrandMentions: false,
    keyFeaturesBenefits: '',
    targetAudience: '',
    competitor1: '',
    competitor2: '',
    competitor3: '',
    closestCompetitor: '',
    competitorLikeDislike: '',
    brandComparison: '',
    brandTonality: '',
    inspirationsReferences: '',
    desiredDesignStyle: '',
    favBrand1Link: '',
    favBrand1Describe: '',
    favBrand2Link: '',
    favBrand2Describe: '',
    favBrand3Link: '',
    favBrand3Describe: '',
  });

  // Preload bg to avoid flash
  useEffect(() => {
    const img = new Image();
    img.src = bgPattern;
    img.onload = () => {};
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (type === 'radio') {
      if (name === 'hasBrandMentions') {
        setFormData(prev => ({ ...prev, hasBrandMentions: value === 'yes' }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value } as any));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // File inputs for two specific fields
  const categoriesFileInputRef = useRef<HTMLInputElement>(null);
  const asinsFileInputRef = useRef<HTMLInputElement>(null);

  const onPickCategoriesFile = () => categoriesFileInputRef.current?.click();
  const onPickAsinsFile = () => asinsFileInputRef.current?.click();

  const onCategoriesFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, productCountAndCategoriesFileName: file.name, productCountAndCategoriesFileUrl: url }));
    }
  };

  const onAsinsFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, productAsinsOrLinksFileName: file.name, productAsinsOrLinksFileUrl: url }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/storefront-review', { state: { formData } });
  };

  const inputStyle: React.CSSProperties = {
    borderRadius: 12,
    border: '1px solid #E2D5FF',
    background: 'rgba(244, 243, 255, 0.60)'
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-[#E9E3FF] to-[#F8F6FF]">
      <div aria-hidden className="fixed inset-0 -z-10" style={{
        backgroundImage: `url(${bgPattern})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transform: 'translateZ(0)',
        pointerEvents: 'none'
      }} />
      <div className="container mx-auto px-4" style={{paddingTop: 144, paddingBottom: 90}}>
        <div className="text-center mb-12">
          <h1 className="font-roboto font-semibold text-3xl text-[#4A3B8E]" style={{ fontSize: 48, marginBottom: 16 }}>Welcome To Storefront Onboarding Form</h1>
          <p className="mb-8" style={{ color: '#4A3B8E', fontSize: 18, fontWeight: 400 }}>Optimize Your Amazon Storefront</p>
        </div>

        <div className="bg-white shadow-md mx-auto p-8" style={{ width: 1160, borderRadius: '20px' }}>
          <div className="mb-8">
            <h2 className="font-roboto font-semibold mb-4" style={{ fontSize: 32, color: '#4A3B8E' }}>Let’s Get Things Rolling</h2>
            <p className="mb-8" style={{ color: '#85878B', fontSize: 18, fontWeight: 400 }}>
              Fill this short form. On the next step you’ll review everything you typed before submitting.
            </p>
          </div>

          <form className="space-y-8" onSubmit={handleSubmit}>
            <div>
              <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Personal Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Enter Your E-mail</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter Your E-mail" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
              </div>
            </div>

            <div>
              <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Company Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What’s your brand name?</label>
                  <input type="text" name="brandName" value={formData.brandName} onChange={handleChange} placeholder="Brand Name" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Do you have a website? If yes, please share the link</label>
                  <input type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">How many products do you have in total? How do you categorise your products within your brand?</label>
                  <div className="flex gap-3 items-center">
                    <input type="url" name="productCountAndCategories" value={formData.productCountAndCategories} onChange={handleChange} placeholder="Drive link (optional)" className="flex-1 px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                    <button type="button" onClick={onPickCategoriesFile} className="px-4 py-2 text-white rounded-lg" style={{ background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)' }}>Upload File</button>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">Only support: .doc, .docx, .xls, .xlsx or Drive link</div>
                  {formData.productCountAndCategoriesFileName && (
                    <div className="text-sm text-[#4A3B8E] mt-1">
                      Uploaded: {formData.productCountAndCategoriesFileName}
                      {formData.productCountAndCategoriesFileUrl && (
                        <a href={formData.productCountAndCategoriesFileUrl} download={formData.productCountAndCategoriesFileName} className="ml-3 underline text-[#564DB8]">Download</a>
                      )}
                    </div>
                  )}
                  <input ref={categoriesFileInputRef} type="file" accept=".doc,.docx,.xls,.xlsx" className="hidden" onChange={onCategoriesFileChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Please share all the PRODUCT ASINs or LINKS and specify the categories and sub-categories (if any)</label>
                  <div className="flex gap-3 items-center">
                    <input type="url" name="productAsinsOrLinks" value={formData.productAsinsOrLinks} onChange={handleChange} placeholder="Drive link (optional)" className="flex-1 px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                    <button type="button" onClick={onPickAsinsFile} className="px-4 py-2 text-white rounded-lg" style={{ background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)' }}>Upload File</button>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">Only support: .doc, .docx, .xls, .xlsx or Drive link</div>
                  {formData.productAsinsOrLinksFileName && (
                    <div className="text-sm text-[#4A3B8E] mt-1">
                      Uploaded: {formData.productAsinsOrLinksFileName}
                      {formData.productAsinsOrLinksFileUrl && (
                        <a href={formData.productAsinsOrLinksFileUrl} download={formData.productAsinsOrLinksFileName} className="ml-3 underline text-[#564DB8]">Download</a>
                      )}
                    </div>
                  )}
                  <input ref={asinsFileInputRef} type="file" accept=".doc,.docx,.xls,.xlsx" className="hidden" onChange={onAsinsFileChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Do you have any Best Sellers/Hero Items within your brand that you'd like us to focus more on? Please specify and share the ASINS or Product Links</label>
                  <textarea name="bestSellersOrHeroes" value={formData.bestSellersOrHeroes} onChange={handleChange} rows={2} placeholder="Share ASINs or product links" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div className="mb-4 flex items-center gap-3">
                  <button
                    type="button"
                    aria-pressed={formData.hasBrandMentions}
                    onClick={() => setFormData(prev => ({ ...prev, hasBrandMentions: !prev.hasBrandMentions }))}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#564DB8] focus:ring-offset-2 ${
                      formData.hasBrandMentions ? 'bg-[#564DB8]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300 ease-in-out ${
                        formData.hasBrandMentions ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-gray-700 font-roboto font-medium" style={{ fontSize: 14 }}>Do you have any brand mentions or media publications to add to the credibility of your brand?</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What are some of the most important features/benefits of your product/s that should be emphasised throughout the landing page?</label>
                  <textarea name="keyFeaturesBenefits" value={formData.keyFeaturesBenefits} onChange={handleChange} rows={3} placeholder="Bulleted list works well" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Please define the target audience for your brand. What are their demographics and preferences?</label>
                  <textarea name="targetAudience" value={formData.targetAudience} onChange={handleChange} rows={2} placeholder="Who are we talking to?" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Provide at least 3 top competitors of your brand.</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input type="text" name="competitor1" value={formData.competitor1} onChange={handleChange} placeholder="Competitor 1" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                  <input type="text" name="competitor2" value={formData.competitor2} onChange={handleChange} placeholder="Competitor 2" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                  <input type="text" name="competitor3" value={formData.competitor3} onChange={handleChange} placeholder="Competitor 3" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Which is the closest competitor we should be looking at?</label>
                  <input type="text" name="closestCompetitor" value={formData.closestCompetitor} onChange={handleChange} placeholder="Closest competitor" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What do you like or dislike about their listings? What makes your product different?</label>
                  <textarea name="competitorLikeDislike" value={formData.competitorLikeDislike} onChange={handleChange} rows={3} placeholder="Your observations" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">How can we compare your brand to your competitors? What makes you a better brand altogether?</label>
                  <textarea name="brandComparison" value={formData.brandComparison} onChange={handleChange} rows={3} placeholder="Short summary" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Tone & Style</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What’s the tonality of your brand? Describe your brand voice with adjectives? (In short, how would like to talk to your customers i.e Professional, Quirky, Excited, Happy, Friendly, Fun, Conversational, Creative, Bold, Elegant etc.)</label>
                  <textarea name="brandTonality" value={formData.brandTonality} onChange={handleChange} rows={2} placeholder="Friendly, bold, elegant..." className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Do you have any inspirations/references for the content? Any past content documents or websites for reference to get a of the writing style you prefer</label>
                  <textarea name="inspirationsReferences" value={formData.inspirationsReferences} onChange={handleChange} rows={2} placeholder="Links or notes" className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What kind of design style would you like to see for your brand?</label>
                  <textarea name="desiredDesignStyle" value={formData.desiredDesignStyle} onChange={handleChange} rows={2} placeholder="Minimal, premium, colorful, etc." className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Can you share any of your favorite brands online whether on or off Amazon that you like in terms of design and style? and what do you like the most about them? Even reference websites will help</h3>
              <div className="space-y-6">
                <div>
                  <div className="text-sm text-gray-600 mb-2">Brand 1</div>
                  <input type="url" name="favBrand1Link" value={formData.favBrand1Link} onChange={handleChange} placeholder="Share link" className="w-full px-3 py-2 mb-3 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                  <textarea name="favBrand1Describe" value={formData.favBrand1Describe} onChange={handleChange} placeholder="Describe" rows={2} className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-2">Brand 2</div>
                  <input type="url" name="favBrand2Link" value={formData.favBrand2Link} onChange={handleChange} placeholder="Share link" className="w-full px-3 py-2 mb-3 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                  <textarea name="favBrand2Describe" value={formData.favBrand2Describe} onChange={handleChange} placeholder="Describe" rows={2} className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-2">Brand 3</div>
                  <input type="url" name="favBrand3Link" value={formData.favBrand3Link} onChange={handleChange} placeholder="Share link" className="w-full px-3 py-2 mb-3 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                  <textarea name="favBrand3Describe" value={formData.favBrand3Describe} onChange={handleChange} placeholder="Describe" rows={2} className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]" style={inputStyle} />
                </div>
              </div>
            </div>

            <div className="pt-8">
              <button type="submit" className="w-full py-3 text-white font-semibold text-lg rounded-lg" style={{
                background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
              }}>Generate Brand Summary</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StorefrontFormPage;


